import React, {useState, useEffect, useRef} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import SearchInput from '../../../common/InlineSearch';
import DataTable from '../../../common/DataTable';
import CustomPagination from '../../../common/Pagination';
import TableRowsPerPage from '../../../common/TableRowsPerPage';
import useBreadcrumbs from '../../../hooks/useBreadCrumbs';
import NavCrumbs from '../../../common/NavCrumbs';
import {USER_HEADERS} from './actions/Constant';
import AddNewUser from './components/AddNewUser';
import {fetchUserList} from '../../../services/UserRegistrationApi';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/Store';
import Stack from 'react-bootstrap/Stack';
import {Container} from 'react-bootstrap';
import Loader from '../../../common/Loader';

const UserRegistration: React.FC = () => {
  // Fetch breadcrumbs for navigation
  const breadcrumbs = useBreadcrumbs();
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);

  // State for managing user data, search, sorting, and pagination
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchQuerylength, setSearchQuerylength] = useState<string>('');
  const searchInputRef = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<any>('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('DESC');

  // Fetch user data from the API
  const fetchUsers = async (
    query: string = '',
    pageNo: number = 1,
    pageSize: number = itemsPerPage,
    key: string = sortColumn,
    direction: string = sortOrder,
  ) => {
    const filterParams = {
      searchString: query,
      pageNo,
      pageSize,
      sortColumn: key,
      sortOrder: direction,
      triggerById: loggedInUserid,
    };

    setLoading(true);
    try {
      const usersData = await fetchUserList(filterParams);
      if (usersData) {
        setData(usersData);
        setFilteredData(usersData);
        setTotalPages(usersData[0]?.totalPages || 1);
        setTotalRows(usersData[0]?.totalCount || null);
      } else {
        console.error('No data found in usersData.result');
      }
    } catch (error) {
      console.error('Error fetching usersData:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when itemsPerPage or currentPage changes
  useEffect(() => {
    fetchUsers(searchQuery, currentPage, itemsPerPage, sortColumn, sortOrder);
  }, [itemsPerPage, currentPage]);

  // Handle sorting actions in the DataTable
  const handleSortAction = (row: any) => {
    setSortColumn(row.key);
    setSortOrder(row.direction);
    fetchUsers(searchQuery, currentPage, itemsPerPage, row.key, row.direction);
  };

  // Handle pagination while keeping the same sorting column
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    fetchUsers(searchQuery, pageNumber, itemsPerPage, sortColumn, sortOrder);
  };

  // Handle actions like Edit/View on a row
  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setOpen(true);
    setSelectedUserData(row);
  };

  // Handle the "Add User" button click
  const handleAddUserClick = () => {
    setAction('Add');
    setSelectedUserData(null);
    setOpen(true);
  };

  // Close Add/Edit form
  const handleCancel = (status: string) => {
    setOpen(false);
    setCurrentPage(1);
    handleReset();
  };

  // Reset the user list and search filters
  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery('');
    setSearchQuerylength('');
    fetchUsers();
  };

  // Update the number of rows displayed per page
  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Handle search query change
  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    fetchUsers(query, 1, itemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  // Calculate text for the range of entries currently visible
  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows
      )} of ${totalRows} entries`;

  return (
    <div>
      <div className='breadcrumbg'>
        <Container fluid className='px-5'>
          <Stack direction='horizontal' gap={2}>
            <div className='pt-0 flex-column d-flex align-items-start fade-in-left'>
              <h4>Users</h4>
              <NavCrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div className='p-2 ms-auto'>
              {!open ? (
                <button
                  onClick={handleAddUserClick}
                  className='btn btn-primary d-flex align-items-center fade-in-right'
                >
                  <span className='material-icons me-2'>add</span>
                  User Registration
                </button>
              ) : (
                ''
              )}
            </div>
          </Stack>
        </Container>
      </div>

      {!open ? (
        <>
          <div className='main_wrapper_section'>
            <div className='container-fluid px-5'>
              <div className='d-flex py-2 search_closer align-items-center'>
                <div className='sectionTitle noborder me-auto '>List</div>
                <SearchInput
                  ref={searchInputRef}
                  data={data}
                  searchKeys={[]}
                  onSearchResult={setFilteredData}
                  placeholder='Search'
                  onSearchQueryChange={handleSearchQueryChange}
                  onInputChange={handleSearchQueryChangeForlength}
                />
                <button
                  onClick={handleReset}
                  className='reset_btn'
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                  }}
                >
                  <span className='material-icons reset_ic0'>restart_alt</span>
                </button>
              </div>
              <div className='borderB mb-3'></div>

              {/* Data Table Section */}
            <div className='adjustHeightTb'>
                <DataTable
                headers={USER_HEADERS}
                rows={filteredData}
                onAction={handleRowAction}
                onSortAction={handleSortAction}
              />
            </div>
              {/* Pagination and Rows Per Page Section */}
              <div className='d-flex justify-content-between align-items-center mt-0'>
                <div className='col-md-12'>
                  <div className='row'>
                    <div className='col-md-3 showingData_wrapper'>
                      <span>{entriesText}</span>
                    </div>
                    <div className='col-md-9'>
                      <div className='d-flex flex-row-reverse '>
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={paginate}
                        />
                        <TableRowsPerPage
                          value={itemsPerPage}
                          onSelect={handleRowsPerPageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <AddNewUser
          userData={selectedUserData}
          action={action}
          onCancel={handleCancel}
          loggedInUserid={loggedInUserid}
        />
      )}
    </div>
  );
};

export default UserRegistration;
